import { toast } from "react-toastify";
import { Component } from "react";
import * as config from "../../config";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";

interface ErrorCodeOption {
    code: string;
    description: string;
}

interface ErrorCodeGroup {
    category: string;
    label: string;
    codes: ErrorCodeOption[];
}

interface SavedPayload {
    id: string;
    name: string;
    template: Record<string, unknown>;
}

interface RegionPreview {
    env: string;
    latitude: number;
    longitude: number;
    currentTime: string;
}

interface KSState {
    deviceID: string;
    payloadType: string;
    payloadTypeOptions: string[];
    vehicleFunction: string;
    functionOptions: string[];

    payloadSource: "" | "new" | "existing";

    payloadTemplate: Record<string, unknown> | null;
    payloadTemplateText: string;
    jsonError: string | null;
    editorMode: "tree" | "raw";
    payloadName: string;

    savedPayloads: SavedPayload[];
    selectedSavedId: string;

    errorCodeGroups: ErrorCodeGroup[];
    errorCode: string;
    notification: string;
    notificationType: "success" | "error";
    loading: boolean;
    lastResult: any;

    // Region preview (read-only display of lat/long + current time — auto-managed, not editable)
    regionPreview: RegionPreview | null;
    protectedFieldSnapshot: Record<string, unknown>;
}

interface KSProps {
    jwtToken: string | null;
}

const PAYLOAD_TYPES = ["Request Result Type Payload", "Vehicle Status Payload"];

const COLORS = {
    bg: "#f4f6f8",
    surface: "#ffffff",
    border: "#e2e8f0",
    borderStrong: "#cbd5e1",
    text: "#1e293b",
    textMuted: "#64748b",
    primary: "#2563eb",
    success: "#16a34a",
    danger: "#dc2626",
    accentBg: "#eff6ff",
};

// Recursively collects { path: value } for every protected field found anywhere in the object.
// Path is built from the key chain so we can detect *which specific* protected value changed,
// even if the same field name appears multiple times (e.g. two PositionInfo blocks).
function collectProtectedFieldValues(
    obj: any,
    path: string = "",
    result: Record<string, unknown> = {}
): Record<string, unknown> {
    if (obj === null || typeof obj !== "object") return result;

    if (Array.isArray(obj)) {
        obj.forEach((item, idx) => collectProtectedFieldValues(item, `${path}[${idx}]`, result));
        return result;
    }

    for (const key of Object.keys(obj)) {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (PROTECTED_FIELD_NAMES.has(key)) {
            result[currentPath] = value;
        }

        if (value && typeof value === "object") {
            collectProtectedFieldValues(value, currentPath, result);
        }
    }
    return result;
}

// Returns the list of paths whose protected values differ between before/after snapshots.
// Returns the list of paths whose protected values differ between before/after snapshots.
function findChangedProtectedFields(
    before: Record<string, unknown>,
    after: Record<string, unknown>
): string[] {
    const changed: string[] = [];
    const allPathsSet = new Set([...Object.keys(before), ...Object.keys(after)]);
    const allPaths = Array.from(allPathsSet);
    for (let i = 0; i < allPaths.length; i++) {
        const path = allPaths[i];
        if (JSON.stringify(before[path]) !== JSON.stringify(after[path])) {
            changed.push(path);
        }
    }
    return changed;
}

// Recursively sets every occurrence of a resultCode-style key (camelCase or PascalCase)
// to the given padded value. Handles nested/repeated structures the same way the
// backend's applyCurrentTimeAndRegionLatLong does for timestamps/lat-long.
const RESULT_CODE_KEYS = new Set(["resultCode", "ResultCode"]);

function applyResultCodeToTemplate(obj: any, paddedCode: string): void {
    if (obj === null || typeof obj !== "object") return;

    if (Array.isArray(obj)) {
        obj.forEach((item) => applyResultCodeToTemplate(item, paddedCode));
        return;
    }

    for (const key of Object.keys(obj)) {
        const value = obj[key];

        if (RESULT_CODE_KEYS.has(key)) {
            obj[key] = paddedCode;
            continue;
        }

        if (value && typeof value === "object") {
            applyResultCodeToTemplate(value, paddedCode);
        }
    }
}
// Pads a result/error code to 4 characters by prefixing with zeros.
// e.g. "63" -> "0063", "DA" -> "00DA", "" -> "" (leave empty untouched)
function padResultCode(code: string): string {
    if (!code) return code;
    return code.length >= 4 ? code : code.padStart(4, "0");
}
// Field names that are always overridden server-side at trigger time — block direct edits
// to these in the Tree editor to keep the UI honest with what will actually be sent.
const PROTECTED_FIELD_NAMES = new Set([
    "dateTime",
    "positionDataAcquisitionDatetime",
    "centerTimestamp",
    "requestReceptionTime",
    "operationEndTime",
    "latitude",
    "longitude",
    "resultCode",
    // PascalCase variants (Trip/UBI/DriveMonitor-style payloads)
    "DataCreationTime",
    "AcquisitionDateTime",
    "Latitude",
    "Longitude",
    "ResultCode",
]);

class KafkaSimulator extends Component<KSProps, KSState> {
    constructor(props: KSProps) {
        super(props);
        this.state = {
            deviceID: "",
            payloadType: "",
            payloadTypeOptions: PAYLOAD_TYPES,
            vehicleFunction: "",
            functionOptions: [],
            payloadSource: "",
            payloadTemplate: null,
            payloadTemplateText: "",
            jsonError: null,
            editorMode: "tree",
            payloadName: "",
            savedPayloads: [],
            selectedSavedId: "",
            errorCodeGroups: [],
            errorCode: "",
            notification: "",
            notificationType: "success",
            loading: false,
            lastResult: null,
            regionPreview: null,
            protectedFieldSnapshot: {},
        };
    }

    componentDidMount() {
        const queryParams = new URLSearchParams(window.location.search);
        const deviceid = queryParams.get("deviceid");
        this.setState({ deviceID: deviceid || "" });
        this.fetchErrorCodes();
        if (deviceid) {
            this.fetchRegionPreview(deviceid);
        }
    }

    showNotification = (message: string, type: "success" | "error" = "success") => {
        this.setState({ notification: message, notificationType: type });
        setTimeout(() => this.setState({ notification: "" }), 4000);
    };

    authHeaders = () => ({
        "x-api-key": config.apiKey,
        Authorization: "Bearer " + this.props.jwtToken,
    });

    // Fetches the region-based lat/long + current-time preview for the active VIN.
    // Purely informational on the frontend — the backend independently enforces these
    // values at trigger time regardless of what is shown/edited here.
    fetchRegionPreview = (vin: string) => {
        fetch(
            config.rootUrl + `/v1/simulator/kafka/region-preview?vin=${encodeURIComponent(vin)}`,
            { method: "GET", headers: this.authHeaders() }
        )
            .then((res) => res.json())
            .then((data) => {
                if (data?.latitude !== undefined) {
                    this.setState({ regionPreview: data });
                } else {
                    this.setState({ regionPreview: null });
                }
            })
            .catch((error) => {
                console.error("Error fetching region preview:", error);
                this.setState({ regionPreview: null });
            });
    };

    // Step 1: Payload type -> functions
    handlePayloadTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const payloadType = event.target.value;
        this.setState({
            payloadType,
            vehicleFunction: "",
            functionOptions: [],
            payloadSource: "",
            payloadTemplate: null,
            payloadTemplateText: "",
            jsonError: null,
            payloadName: "",
            savedPayloads: [],
            selectedSavedId: "",
            errorCode: "",
        });
        this.fetchFunctionOptions(payloadType);
    };

    fetchFunctionOptions = (payloadType: string) => {
        this.setState({ loading: true });
        fetch(
            config.rootUrl +
            `/v1/simulator/kafka/functions?payloadType=${encodeURIComponent(payloadType)}`,
            { method: "GET", headers: this.authHeaders() }
        )
            .then((res) => res.json())
            .then((data) => this.setState({ functionOptions: data?.functions || [], loading: false }))
            .catch((error) => {
                console.error("Error fetching function options:", error);
                this.setState({ functionOptions: [], loading: false });
                toast.error("Error loading function list");
            });
    };

    // Step 2: Function -> reset source step
    handleFunctionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const vehicleFunction = event.target.value;
        this.setState({
            vehicleFunction,
            payloadSource: "",
            payloadTemplate: null,
            payloadTemplateText: "",
            jsonError: null,
            payloadName: "",
            savedPayloads: [],
            selectedSavedId: "",
            errorCode: "",
        });
    };

    // Step 3: Payload source (new / existing)
    handlePayloadSourceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const payloadSource = event.target.value as "" | "new" | "existing";
        this.setState({
            payloadSource,
            payloadTemplate: null,
            payloadTemplateText: "",
            jsonError: null,
            payloadName: "",
            savedPayloads: [],
            selectedSavedId: "",
            errorCode: "",
        });

        if (payloadSource === "new") {
            this.fetchPayloadTemplate(this.state.payloadType, this.state.vehicleFunction);
        } else if (payloadSource === "existing") {
            this.fetchSavedPayloads(this.state.payloadType, this.state.vehicleFunction);
        }
    };

    fetchPayloadTemplate = (payloadType: string, vehicleFunction: string) => {
        this.setState({ loading: true });
        const { deviceID } = this.state;
        fetch(
            config.rootUrl +
            `/v1/simulator/kafka/payload-template?payloadType=${encodeURIComponent(
                payloadType
            )}&function=${encodeURIComponent(vehicleFunction)}&vin=${encodeURIComponent(deviceID)}`,
            { method: "GET", headers: this.authHeaders() }
        )
            .then((res) => res.json())
            .then((data) => {
                const template = data?.template || null;
                this.setState({
                    payloadTemplate: template,
                    payloadTemplateText: template ? JSON.stringify(template, null, 2) : "",
                    jsonError: null,
                    loading: false,
                    protectedFieldSnapshot: template ? collectProtectedFieldValues(template) : {},
                });
                if (this.state.deviceID) {
                    this.fetchRegionPreview(this.state.deviceID);
                }
            })
            .catch((error) => {
                console.error("Error fetching payload template:", error);
                this.setState({
                    payloadTemplate: null,
                    payloadTemplateText: "",
                    jsonError: null,
                    loading: false,
                    protectedFieldSnapshot: {},
                });
                toast.error("Error loading payload JSON template");
            });
    };

    fetchSavedPayloads = (payloadType: string, vehicleFunction: string) => {
        this.setState({ loading: true });
        fetch(
            config.rootUrl +
            `/v1/simulator/kafka/saved-payloads?payloadType=${encodeURIComponent(
                payloadType
            )}&function=${encodeURIComponent(vehicleFunction)}`,
            { method: "GET", headers: this.authHeaders() }
        )
            .then((res) => res.json())
            .then((data) => this.setState({ savedPayloads: data?.payloads || [], loading: false }))
            .catch((error) => {
                console.error("Error fetching saved payloads:", error);
                this.setState({ savedPayloads: [], loading: false });
                toast.error("Error loading saved payloads");
            });
    };

    handleSavedSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedSavedId = event.target.value;
        const saved = this.state.savedPayloads.find((p) => p.id === selectedSavedId);
        this.setState({
            selectedSavedId,
            payloadTemplate: saved ? saved.template : null,
            payloadTemplateText: saved ? JSON.stringify(saved.template, null, 2) : "",
            jsonError: null,
            protectedFieldSnapshot: saved ? collectProtectedFieldValues(saved.template) : {},
        });
        if (this.state.deviceID) {
            this.fetchRegionPreview(this.state.deviceID);
        }
    };

    fetchErrorCodes = () => {
        fetch(config.rootUrl + `/v1/simulator/kafka/error-codes`, {
            method: "GET",
            headers: this.authHeaders(),
        })
            .then((res) => res.json())
            .then((data) => this.setState({ errorCodeGroups: data?.groups || [] }))
            .catch((error) => {
                console.error("Error fetching error codes:", error);
                this.setState({ errorCodeGroups: [] });
            });
    };

    handleErrorCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ errorCode: event.target.value });
    };

    // Raw JSON editing: validate on every keystroke, keep the error visible,
    // and only update payloadTemplate when the text actually parses.
    // Note: protected fields (timestamps/lat-long/resultCode) are always overwritten
    // server-side at trigger time regardless of what is typed here — see the info note.
    handleTemplateTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = event.target.value;

        try {
            const parsed = JSON.parse(text);
            if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
                this.setState({ payloadTemplateText: text, jsonError: "Payload must be a JSON object" });
                return;
            }

            // Check whether any protected field's value was changed vs. the loaded baseline
            const afterSnapshot = collectProtectedFieldValues(parsed);
            const changedPaths = findChangedProtectedFields(
                this.state.protectedFieldSnapshot,
                afterSnapshot
            );

            if (changedPaths.length > 0) {
                // Reject the edit outright: keep old text/template, show a clear error,
                // and do not let the invalid (tampered) text persist in the box.
                toast.error(
                    `Editing these fields is not allowed — they are auto-set at trigger time: ${changedPaths
                        .map((p) => p.split(".").pop())
                        .join(", ")}`
                );
                this.setState({ jsonError: "Protected field(s) modified — edit rejected" });
                return;
            }

            this.setState({ payloadTemplateText: text, payloadTemplate: parsed, jsonError: null });
        } catch (e: any) {
            // Still let them type freely while JSON is mid-edit/invalid — only enforce
            // the protected-field check once it's valid JSON again.
            this.setState({ payloadTemplateText: text, jsonError: e.message || "Invalid JSON" });
        }
    };

    // Tree editing: reject edits to protected fields (timestamps/lat-long/resultCode) —
    // these are always auto-set at trigger time, so editing them here would be misleading.
    handleTreeEdit = (opts: any) => {
        const updated = opts?.src;
        if (!updated) return;

        const editedKey = opts?.key ?? opts?.name;
        if (editedKey && PROTECTED_FIELD_NAMES.has(String(editedKey))) {
            toast.error(
                `"${editedKey}" is auto-set at trigger time (current time / region lat-long / selected error code) and cannot be edited here.`
            );
            return; // reject the edit — do not update state
        }

        this.setState({
            payloadTemplate: { ...updated },
            payloadTemplateText: JSON.stringify(updated, null, 2),
            jsonError: null,
        });
    };

    setEditorMode = (mode: "tree" | "raw") => this.setState({ editorMode: mode });

    // Central guard: returns the parsed, valid template, or null (and toasts) if invalid
    getValidTemplateOrWarn = (): Record<string, unknown> | null => {
        const { editorMode, payloadTemplateText, payloadTemplate, jsonError, protectedFieldSnapshot } = this.state;

        let candidate: Record<string, unknown> | null = null;

        if (editorMode === "raw") {
            if (jsonError) {
                toast.error(`Invalid JSON — fix it before continuing: ${jsonError}`);
                return null;
            }
            try {
                const parsed = JSON.parse(payloadTemplateText);
                if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
                    toast.error("Payload must be a JSON object.");
                    return null;
                }
                candidate = parsed;
            } catch (e: any) {
                toast.error(`Invalid JSON — fix it before continuing: ${e.message}`);
                return null;
            }
        } else {
            if (!payloadTemplate) {
                toast.error("Payload JSON is empty or invalid");
                return null;
            }
            candidate = payloadTemplate;
        }

        // Final backstop: reject if protected fields differ from the loaded baseline
        const afterSnapshot = collectProtectedFieldValues(candidate);
        const changedPaths = findChangedProtectedFields(protectedFieldSnapshot, afterSnapshot);
        if (changedPaths.length > 0) {
            toast.error(
                `Cannot proceed — protected field(s) were modified: ${changedPaths
                    .map((p) => p.split(".").pop())
                    .join(", ")}. These are auto-set at trigger time.`
            );
            return null;
        }

        return candidate;
    };

    // Publish to the VIN (shared by both flows)
// Publish to the VIN (shared by both flows)
    triggerPayload = (templateOverride?: Record<string, unknown>, includeErrorCode: boolean = true): Promise<any> => {
        const { deviceID, payloadType, vehicleFunction, payloadTemplate, errorCode } = this.state;
        const body = {
            vin: deviceID,
            payloadType,
            vehicleFunction,
            template: templateOverride ?? payloadTemplate,
            errorCode: includeErrorCode ? (errorCode || null) : null,
            environment: localStorage.getItem("selectedEnv"),
        };
        return fetch(config.rootUrl + "/v1/simulator/kafka/trigger", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...this.authHeaders() },
            body: JSON.stringify(body),
        }).then((res) => {
            if (!res.ok) throw new Error("Trigger request failed");
            return res.json();
        });
    };

    // Existing flow: just trigger
// Existing flow: just trigger — uses the saved payload's own Result Code as-is, no override
    handleTrigger = () => {
        const { payloadType, vehicleFunction } = this.state;
        if (!payloadType || !vehicleFunction) {
            toast.error("Please select a payload before triggering");
            return;
        }

        const template = this.getValidTemplateOrWarn();
        if (!template) return;

        this.setState({ loading: true });
        this.triggerPayload(template, false)
            .then((data) => {
                this.setState({ loading: false, lastResult: data });
                toast.success(
                    "Payload published successfully! Timestamps and lat/long were applied automatically."
                );
            })
            .catch((error) => {
                console.error("Error triggering payload:", error);
                this.setState({ loading: false });
                toast.error(
                    "Error publishing payload. Please check VIN status and try again."
                );
            });
    };

    // New flow: save the payload, then trigger
    handleSaveAndTrigger = () => {
        const { payloadType, vehicleFunction, payloadName, errorCode } = this.state;

        const template = this.getValidTemplateOrWarn();
        if (!template) return;

        if (!payloadName.trim()) {
            toast.error("Please enter a name for the payload");
            return;
        }

        // Pad the selected Result Code to 4 characters (e.g. "63" -> "0063") and inject it
        // into the template before saving. This mutates a deep copy so the visible editor
        // state / protectedFieldSnapshot baseline isn't touched.
        const templateToSave = JSON.parse(JSON.stringify(template));
        const paddedResultCode = padResultCode(errorCode);
        if (paddedResultCode) {
            applyResultCodeToTemplate(templateToSave, paddedResultCode);
        }

        this.setState({ loading: true });

        const saveBody = {
            payloadType,
            vehicleFunction,
            name: payloadName.trim(),
            template: templateToSave,
        };

        fetch(config.rootUrl + "/v1/simulator/kafka/save-payload", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...this.authHeaders() },
            body: JSON.stringify(saveBody),
        })
            .then((res) => {
                if (!res.ok) throw new Error("Save request failed");
                return res.json();
            })
            .then(() => this.triggerPayload(templateToSave))
            .then((data) => {
                this.setState({ loading: false, lastResult: data });
                toast.success(
                    "Payload saved and published successfully! Timestamps, lat/long, and result code were applied automatically."
                );
            })
            .catch((error) => {
                console.error("Error saving/triggering payload:", error);
                this.setState({ loading: false });
                toast.error("Error saving or publishing payload.");
            });
    };

    renderInfoNote() {
        const { regionPreview } = this.state;
        return (
            <div style={styles.infoNote}>
                <strong>Note:</strong> Regardless of the values shown or edited below — the timestamp
                fields (<code>dateTime</code>, <code>positionDataAcquisitionDatetime</code>,{" "}
                <code>centerTimestamp</code>, <code>requestReceptionTime</code>,{" "}
                <code>operationEndTime</code>) will always be set to the{" "}
                <strong>current time</strong> at trigger time. Latitude/Longitude will always be set{" "}
                <strong>automatically based on the VIN's region</strong>
                {regionPreview && (
                    <>
                        {" "}
                        (currently resolved to{" "}
                        <strong>
                            {regionPreview.latitude}, {regionPreview.longitude}
                        </strong>{" "}
                        for region <strong>{regionPreview.env}</strong>)
                    </>
                )}
                . The Result Code that gets sent will always be whatever is selected in the dropdown
                below, and that selection is saved for this VIN.
            </div>
        );
    }

    renderEditor() {
        const { payloadTemplate, payloadTemplateText, editorMode, jsonError, loading } = this.state;

        if (loading && !payloadTemplateText) {
            return <div style={styles.loadingBox}>Loading JSON template...</div>;
        }

        return (
            <>
                <div style={styles.recordHeader}>
                    <h3 style={styles.cardTitle}>Payload JSON</h3>
                    <div style={styles.toggleGroup}>
                        <button
                            onClick={() => this.setEditorMode("tree")}
                            style={{
                                ...styles.toggleBtn,
                                ...(editorMode === "tree" ? styles.toggleBtnActive : {}),
                            }}
                        >
                            Tree
                        </button>
                        <button
                            onClick={() => this.setEditorMode("raw")}
                            style={{
                                ...styles.toggleBtn,
                                ...(editorMode === "raw" ? styles.toggleBtnActive : {}),
                            }}
                        >
                            Raw JSON
                        </button>
                    </div>
                </div>

                {this.renderInfoNote()}

                {editorMode === "tree" ? (
                    <div style={styles.jsonWrapper}>
                        {payloadTemplate ? (
                            <JsonView
                                src={payloadTemplate}
                                collapsed={2}
                                editable={true}
                                enableClipboard={false}
                                onEdit={this.handleTreeEdit}
                                onAdd={this.handleTreeEdit}
                                onDelete={this.handleTreeEdit}
                                style={{
                                    fontSize: "13px",
                                    fontFamily: "'SFMono-Regular', Consolas, Menlo, monospace",
                                }}
                                className="github"
                            />
                        ) : (
                            <div style={styles.loadingBox}>
                                No valid JSON. Switch to Raw JSON to fix it.
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <textarea
                            value={payloadTemplateText}
                            onChange={this.handleTemplateTextChange}
                            rows={18}
                            style={{
                                ...styles.textarea,
                                ...(jsonError ? styles.textareaInvalid : {}),
                            }}
                        />
                        {jsonError && (
                            <div style={styles.jsonErrorText}>Invalid JSON: {jsonError}</div>
                        )}
                    </>
                )}
            </>
        );
    }

 renderErrorCodeSelect() {
        const { errorCodeGroups, errorCode } = this.state;
        if (errorCodeGroups.length === 0) return null;
        return (
            <div style={{ ...styles.field, maxWidth: "600px", marginTop: "20px" }}>
                <label style={styles.label}>Result Code</label>
                <select value={errorCode} onChange={this.handleErrorCodeChange} style={styles.select}>
                    <option value="">None</option>
                    {errorCodeGroups.map((group) => (
                        <optgroup key={group.category} label={group.label}>
                            {group.codes.map((opt) => (
                                <option key={`${group.category}-${opt.code}`} value={opt.code}>
                                    {opt.code} : {opt.description}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                <div style={styles.fieldHint}>
                    The selected code will be zero-padded to 4 characters (e.g. "63" → "0063"),
                    saved into this payload's Result Code, and used when triggered.
                </div>
            </div>
        );
    }

    render() {
        const {
            payloadType,
            payloadTypeOptions,
            vehicleFunction,
            functionOptions,
            payloadSource,
            payloadName,
            savedPayloads,
            selectedSavedId,
            payloadTemplate,
            jsonError,
            editorMode,
            notification,
            notificationType,
            loading,
            deviceID,
            regionPreview,
        } = this.state;

        const isError = notificationType === "error";
        const isJsonInvalid = editorMode === "raw" && !!jsonError;

        return (
            <div style={styles.page}>
                <div style={styles.container}>
               <header style={styles.header}>
                        <div>
                            <h1 style={styles.title}>Kafka Simulator Dashboard</h1>
                            <p style={styles.subtitle}>
                                Build, edit, and publish simulated Kafka payloads to a vehicle.
                            </p>
                        </div>
                        <div style={styles.badgeGroup}>
                            <span style={styles.vinBadge}>VIN: {deviceID || "—"}</span>
                            <span style={styles.envBadge}>Env: {regionPreview?.env ?? "—"}</span>
                        </div>
                    </header>

                    {notification && (
                        <div
                            style={{
                                ...styles.notification,
                                backgroundColor: isError ? "#fef2f2" : "#f0fdf4",
                                borderColor: isError ? "#fecaca" : "#bbf7d0",
                                color: isError ? COLORS.danger : COLORS.success,
                            }}
                        >
                            <span style={styles.notificationDot(isError ? COLORS.danger : COLORS.success)} />
                            {notification}
                        </div>
                    )}

                    {/* Selection card */}
                    <section style={styles.card}>
                        <h2 style={styles.cardTitle}>Payload Configuration</h2>
                        <div style={styles.selectorRow}>
                            <div style={styles.field}>
                                <label style={styles.label}>Payload Type</label>
                                <select
                                    value={payloadType}
                                    onChange={this.handlePayloadTypeChange}
                                    style={styles.select}
                                >
                                    <option value="" disabled>Select Payload Type</option>
                                    {payloadTypeOptions.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label}>Function</label>
                                <select
                                    value={vehicleFunction}
                                    onChange={this.handleFunctionChange}
                                    disabled={!payloadType || loading || functionOptions.length === 0}
                                    style={{ ...styles.select, ...(!payloadType ? styles.selectDisabled : {}) }}
                                >
                                    <option value="" disabled>
                                        {!payloadType
                                            ? "Select payload type first"
                                            : functionOptions.length === 0
                                                ? "Loading functions..."
                                                : "Select Function"}
                                    </option>
                                    {functionOptions.map((fn) => (
                                        <option key={fn} value={fn}>{fn}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Intermediate step: payload source */}
                            {vehicleFunction && (
                                <div style={styles.field}>
                                    <label style={styles.label}>Payload Source</label>
                                    <select
                                        value={payloadSource}
                                        onChange={this.handlePayloadSourceChange}
                                        style={styles.select}
                                    >
                                        <option value="" disabled>Select Source</option>
                                        <option value="new">Create New Payload</option>
                                        <option value="existing">Select Existing Payload</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* CREATE NEW */}
                    {vehicleFunction && payloadSource === "new" && (
                        <section style={styles.card}>
                            <div style={{ ...styles.field, maxWidth: "400px", marginBottom: "20px" }}>
                                <label style={styles.label}>Payload Name</label>
                                <input
                                    type="text"
                                    value={payloadName}
                                    onChange={(e) => this.setState({ payloadName: e.target.value })}
                                    placeholder="e.g. Lock success scenario"
                                    style={styles.input}
                                />
                            </div>

                            {this.renderEditor()}
                            {this.renderErrorCodeSelect()}

                            <div style={styles.buttonRow}>
                                <button
                                    onClick={this.handleSaveAndTrigger}
                                    disabled={loading || isJsonInvalid}
                                    title={isJsonInvalid ? "Fix invalid JSON before saving" : undefined}
                                    style={{
                                        ...styles.triggerBtn,
                                        backgroundColor:
                                            loading || isJsonInvalid ? "#94a3b8" : COLORS.success,
                                        cursor: loading || isJsonInvalid ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {loading ? "Working..." : "Save & Trigger"}
                                </button>
                            </div>
                        </section>
                    )}

                    {/* SELECT EXISTING */}
                    {/* SELECT EXISTING */}
                    {vehicleFunction && payloadSource === "existing" && (
                        <section style={styles.card}>
                            <div style={{ ...styles.field, maxWidth: "500px", marginBottom: "20px" }}>
                                <label style={styles.label}>Existing Payload</label>
                                <select
                                    value={selectedSavedId}
                                    onChange={this.handleSavedSelect}
                                    disabled={loading}
                                    style={styles.select}
                                >
                                    <option value="">
                                        {savedPayloads.length === 0
                                            ? "No saved payloads found"
                                            : "Select a saved payload"}
                                    </option>
                                    {savedPayloads.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {payloadTemplate && (
                                <>
                                    {this.renderInfoNote()}
                                    <div style={styles.jsonWrapper}>
                                        <JsonView
                                            src={payloadTemplate}
                                            collapsed={2}
                                            enableClipboard={false}
                                            style={{
                                                fontSize: "13px",
                                                fontFamily: "'SFMono-Regular', Consolas, Menlo, monospace",
                                            }}
                                            className="github"
                                        />
                                    </div>
                                    {/* Result Code dropdown intentionally omitted here —
                                        existing payloads use their own already-saved Result Code as-is. */}
                                    <div style={styles.buttonRow}>
                                        <button
                                            onClick={this.handleTrigger}
                                            disabled={loading}
                                            style={{
                                                ...styles.triggerBtn,
                                                backgroundColor: loading ? "#94a3b8" : COLORS.primary,
                                                cursor: loading ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            {loading ? "Publishing..." : "Trigger Kafka Payload"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </section>
                    )}
                </div>
            </div>
        );
    }
}

const styles: { [key: string]: any } = {
    page: {
        minHeight: "100%",
        backgroundColor: COLORS.bg,
        padding: "32px 16px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: COLORS.text,
    },
    container: { maxWidth: "1080px", margin: "0 auto" },
    header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px" },
    title: { fontSize: "26px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" },
    subtitle: { fontSize: "14px", color: COLORS.textMuted, margin: "6px 0 0" },
vinBadge: {
        fontSize: "13px", fontWeight: 600, color: COLORS.primary, backgroundColor: COLORS.accentBg,
        border: "1px solid #bfdbfe", padding: "8px 14px", borderRadius: "999px", whiteSpace: "nowrap",
    },
    badgeGroup: {
        display: "flex",
        gap: "10px",
        alignItems: "center",
        flexWrap: "wrap",
    },
    envBadge: {
        fontSize: "13px", fontWeight: 600, color: "#166534", backgroundColor: "#f0fdf4",
        border: "1px solid #bbf7d0", padding: "8px 14px", borderRadius: "999px", whiteSpace: "nowrap",
    },
    notification: {
        display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px",
        borderRadius: "10px", border: "1px solid", fontSize: "14px", fontWeight: 500, marginBottom: "20px",
    },
    notificationDot: (color: string) => ({ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }),
    card: {
        backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: "14px",
        padding: "24px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    },
    cardTitle: { fontSize: "16px", fontWeight: 600, margin: 0 },
    selectorRow: { display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "16px" },
    field: { flex: 1, minWidth: "240px" },
    label: { display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: 600, color: COLORS.textMuted },
    select: {
        width: "100%", padding: "10px 36px 10px 12px", fontSize: "14px", color: COLORS.text,
        border: `1px solid ${COLORS.borderStrong}`, borderRadius: "8px", backgroundColor: "#fff",
        outline: "none", cursor: "pointer", appearance: "none",
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
        backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
    },
    selectDisabled: { backgroundColor: "#f8fafc", color: "#94a3b8", cursor: "not-allowed" },
    input: {
        width: "100%", padding: "10px 12px", fontSize: "14px", color: COLORS.text,
        border: `1px solid ${COLORS.borderStrong}`, borderRadius: "8px", backgroundColor: "#fff",
        outline: "none", boxSizing: "border-box",
    },
    recordHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "12px" },
    toggleGroup: { display: "inline-flex", border: `1px solid ${COLORS.borderStrong}`, borderRadius: "8px", overflow: "hidden" },
    toggleBtn: { padding: "6px 16px", fontSize: "13px", fontWeight: 600, border: "none", backgroundColor: "#fff", color: COLORS.textMuted, cursor: "pointer" },
    toggleBtnActive: { backgroundColor: COLORS.primary, color: "#fff" },
    jsonWrapper: {
        backgroundColor: "#fbfcfe", borderRadius: "10px", border: `1px solid ${COLORS.border}`,
        padding: "16px", maxWidth: "100%", overflowX: "auto", wordBreak: "break-word",
    },
    textarea: {
        width: "100%", fontFamily: "'SFMono-Regular', Consolas, Menlo, monospace", fontSize: "13px",
        padding: "14px", border: `1px solid ${COLORS.borderStrong}`, borderRadius: "10px",
        backgroundColor: "#fbfcfe", resize: "vertical", boxSizing: "border-box", outline: "none", color: COLORS.text,
    },
    textareaInvalid: {
        borderColor: COLORS.danger,
        backgroundColor: "#fef2f2",
    },
    jsonErrorText: {
        fontSize: "12px",
        color: COLORS.danger,
        marginTop: "8px",
        fontWeight: 500,
    },
    infoNote: {
        fontSize: "12.5px",
        color: COLORS.textMuted,
        backgroundColor: COLORS.accentBg,
        border: "1px solid #bfdbfe",
        borderRadius: "8px",
        padding: "10px 14px",
        marginBottom: "16px",
        lineHeight: 1.5,
    },
    fieldHint: {
        fontSize: "11.5px",
        color: COLORS.textMuted,
        marginTop: "6px",
    },
    loadingBox: { fontSize: "13px", color: COLORS.textMuted, padding: "24px", textAlign: "center" },
    buttonRow: { display: "flex", gap: "12px", marginTop: "24px", flexWrap: "wrap" },
    triggerBtn: { padding: "12px 28px", fontSize: "14px", fontWeight: 600, border: "none", borderRadius: "8px", color: "#fff" },
};

export default KafkaSimulator;
